import { Query } from '@apollo/client/react/components';
import { KeySet } from '@tableau/data-structures';
import React from 'react';
import {
  DatasourceLineageCountsFilteredByFieldQuery, DatasourceLineageCountsFilteredByFieldQueryVariables, DatasourceLineageCountsQuery, DatasourceLineageCountsQueryVariables,
  DatasourceLineageCountsFilteredByFieldDocument,
  DatasourceLineageCountsDocument
} from '../../../../../gen-src/queries';
import { LineageCountsParentProps } from '../lineage/LineageCountsCacher';
import { renderFilteredLineageCounts, renderUnfilteredLineageCounts } from '../lineage/LineageCountsFunctions';

export type DatasourceLineageCounts = {
  readonly filteredFieldName: string | null;
  readonly fields: number;
  readonly upstreamDatabases: number;
  readonly upstreamTables: number;
  readonly upstreamVirtualConnections: number;
  readonly upstreamVirtualConnectionTables: number;
  readonly upstreamFlows: number;
  readonly upstreamDatasources: number;
  readonly downstreamDatabases: number;
  readonly downstreamTables: number;
  readonly downstreamFlows: number;
  readonly downstreamDatasources: number;
  readonly downstreamLenses: number;
  readonly downstreamWorkbooks: number;
  readonly downstreamDashboards: number;
  readonly downstreamSheets: number;
  readonly downstreamMetrics: number;
  readonly downstreamOwners: number;
};

function createUnfilteredCounts(data: DatasourceLineageCountsQuery): DatasourceLineageCounts | null {
  const datasource = data.publishedDatasources && data.publishedDatasources[0];
  if (datasource
    && datasource.unfilteredFieldsConnection
    && datasource.upstreamDatabasesConnection
    && datasource.upstreamTablesConnection
    && datasource.upstreamFlowsConnection
    && datasource.upstreamDatasourcesConnection
    && datasource.downstreamDatabasesConnection
    && datasource.downstreamTablesConnection
    && datasource.downstreamFlowsConnection
    && datasource.downstreamDatasourcesConnection
    && datasource.downstreamWorkbooksConnection
    && datasource.downstreamSheetsConnection
    && datasource.downstreamMetricsConnection
    && datasource.downstreamDashboardsConnection
    && datasource.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: null,
      fields: datasource.unfilteredFieldsConnection.totalCount,
      upstreamDatabases: datasource.upstreamDatabasesConnection.totalCount,
      upstreamTables: datasource.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: datasource.upstreamVirtualConnectionsConnection?.totalCount || 0,
      upstreamVirtualConnectionTables: datasource.upstreamVirtualConnectionTablesConnection?.totalCount || 0,
      upstreamFlows: datasource.upstreamFlowsConnection.totalCount,
      upstreamDatasources: datasource.upstreamDatasourcesConnection.totalCount,
      downstreamDatabases: datasource.downstreamDatabasesConnection.totalCount,
      downstreamTables: datasource.downstreamTablesConnection.totalCount,
      downstreamFlows: datasource.downstreamFlowsConnection.totalCount,
      downstreamDatasources: datasource.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: datasource.downstreamLensesConnection?.totalCount || 0,
      downstreamWorkbooks: datasource.downstreamWorkbooksConnection.totalCount,
      downstreamDashboards: datasource.downstreamDashboardsConnection.totalCount,
      downstreamSheets: datasource.downstreamSheetsConnection.totalCount,
      downstreamMetrics: datasource.downstreamMetricsConnection.totalCount,
      downstreamOwners: datasource.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

function createFilteredCounts(data: DatasourceLineageCountsFilteredByFieldQuery): DatasourceLineageCounts | null {
  const datasource = data.publishedDatasources && data.publishedDatasources[0];
  const filteredField = datasource && datasource.filteredFieldsConnection && datasource.filteredFieldsConnection.nodes[0];
  if (datasource
    && datasource.unfilteredFieldsConnection
    && filteredField
    && filteredField.upstreamDatabasesConnection
    && filteredField.upstreamTablesConnection
    && filteredField.upstreamFlowsConnection
    && filteredField.upstreamDatasourcesConnection
    && filteredField.downstreamDatabasesConnection
    && filteredField.downstreamTablesConnection
    && filteredField.downstreamFlowsConnection
    && filteredField.downstreamDatasourcesConnection
    && filteredField.downstreamWorkbooksConnection
    && filteredField.downstreamDashboardsConnection
    && filteredField.downstreamSheetsConnection
    && filteredField.downstreamMetricsConnection
    && filteredField.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: filteredField?.name ?? null,
      fields: datasource.unfilteredFieldsConnection.totalCount,
      upstreamDatabases: filteredField.upstreamDatabasesConnection.totalCount,
      upstreamTables: filteredField.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: filteredField.upstreamVirtualConnectionsConnection?.totalCount || 0,
      upstreamVirtualConnectionTables: filteredField.upstreamVirtualConnectionTablesConnection?.totalCount || 0,
      upstreamFlows: filteredField.upstreamFlowsConnection.totalCount,
      upstreamDatasources: filteredField.upstreamDatasourcesConnection.totalCount,
      downstreamDatabases: filteredField.downstreamDatabasesConnection.totalCount,
      downstreamTables: filteredField.downstreamTablesConnection.totalCount,
      downstreamFlows: filteredField.downstreamFlowsConnection.totalCount,
      downstreamDatasources: filteredField.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: filteredField.downstreamLensesConnection?.totalCount || 0,
      downstreamWorkbooks: filteredField.downstreamWorkbooksConnection.totalCount,
      downstreamDashboards: filteredField.downstreamDashboardsConnection.totalCount,
      downstreamSheets: filteredField.downstreamSheetsConnection.totalCount,
      downstreamMetrics: filteredField.downstreamMetricsConnection.totalCount,
      downstreamOwners: filteredField.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

export type DatasourceLineageCountsQueryWrapperProps = {
  readonly vizportalUrlId: string;
  readonly selectedFieldKeys: KeySet;
} & LineageCountsParentProps<DatasourceLineageCounts>;

export const DatasourceLineageCountsQueryWrapper = (props: DatasourceLineageCountsQueryWrapperProps) => {
  const selectedFields = Object.keys(props.selectedFieldKeys);
  if (selectedFields.length > 0) {
    const selectedFieldId = selectedFields[0];
    return (
      <Query<DatasourceLineageCountsFilteredByFieldQuery, DatasourceLineageCountsFilteredByFieldQueryVariables>
        query={DatasourceLineageCountsFilteredByFieldDocument}
        variables={{
          vizportalUrlId: props.vizportalUrlId,
          selectedFieldId: selectedFieldId,
        }}
      >
        {result => <>{renderFilteredLineageCounts(result, createUnfilteredCounts, createFilteredCounts, props.children)}</>}
      </Query>
    );
  } else {
    return (
      <Query<DatasourceLineageCountsQuery, DatasourceLineageCountsQueryVariables>
        query={DatasourceLineageCountsDocument}
        variables={{
          vizportalUrlId: props.vizportalUrlId,
        }}
      >
        {result => <>{renderUnfilteredLineageCounts(result, createUnfilteredCounts, props.children)}</>}
      </Query>
    );
  }
}
